import {assign} from 'lodash';
import * as React from 'react';
import {balloon} from '@packages/utilities/svg-path';
import type {PlacementIdentifier} from '@packages/utilities/box';
import {emptyObject} from '@packages/utilities/fp/utils';
import {useThemeContext} from '@packages/utilities/styled';

type StyleObject = {
  width: number;
  height: number;
  color: string;
  borderRadius: number;
};
export type Props = {
  style?: StyleObject;
  position?: PlacementIdentifier;
  alternate?: boolean;
  arrowSize?: number;
  arrowOffset?: number;
  dropShadow?: boolean;
  dropShadowLight?: boolean;
  canvasShadow?: boolean;
  borderWidth?: number;
  borderColor?: string;
  caretBackgroundColor?: string;
  fillParentDimensions?: boolean;
  id?: string;
};

const darkModeShadow =
  'drop-shadow(0 0.5px 0 rgba(0,0,0,0.6)) drop-shadow(0 2px 6px rgba(0,0,0,0.18)) drop-shadow(0 4px 8px rgba(0,0,0,0.12)) drop-shadow(0 8px 16px rgba(0,0,0,0.08)) drop-shadow(0 12px 25px rgba(0,0,0,0.06))';

const lightModeShadow =
  'drop-shadow(0 0.5px 0 rgba(0,0,0,0.1)) drop-shadow(0 2px 6px rgba(0,0,0,0.18)) drop-shadow(0 9px 8px rgba(0,0,0,0.12)) drop-shadow(0 8px 16px rgba(0,0,0,0.08)) drop-shadow(0 12px 25px rgba(0,0,0,0.06))';

const Balloon = ({
  style: rawStyle = {
    width: 0,
    height: 0,
    color: 'transparent',
    borderRadius: 0,
  },

  position = 'top',
  arrowSize = 10,
  arrowOffset = 150,
  dropShadow = false,
  dropShadowLight = false,
  canvasShadow = false,
  caretBackgroundColor,
  borderColor: _borderColor,
  fillParentDimensions = false,
  id,
}: Props) => {
  const {__internalTheme} = useThemeContext();
  const filterShadow = __internalTheme?.toLowerCase?.()?.includes('light')
    ? lightModeShadow
    : darkModeShadow;
  const shadowStyle = {filter: filterShadow} as const;
  const shadowLightStyle = {filter: filterShadow} as const;
  const fallbackShadowStyle = shadowStyle;
  const fallbackLightShadowStyle = shadowLightStyle;
  const canvasShadowStyle = shadowStyle;

  const borderColor = _borderColor || 'black';
  const style = {
    ...rawStyle,
    width: Math.ceil(rawStyle.width),
    height: Math.ceil(rawStyle.height),
  } as const;

  const balloonPath = balloon({
    position: position || 'top',
    width: style.width,
    height: style.height,
    borderRadius: style.borderRadius || 0,
    arrowSize,
    arrowOffset,
  });

  // FIXME: SVG clip path IDs are globally-scoped. 😭
  const balloonClipID = 'balloon-clip-' + Math.floor(Math.random() * 100000);
  const balloonStyles = assign(
    {
      display: 'block',
      overflow: 'visible',
    },
    style,
    dropShadow ? shadowStyle : fallbackShadowStyle,
    dropShadowLight ? shadowLightStyle : fallbackLightShadowStyle,
    canvasShadow ? canvasShadowStyle : emptyObject,
    {borderRadius: 0},
    fillParentDimensions ? {height: '100%', width: '100%'} : emptyObject
  );

  const balloonStrokeWidth = 0;
  /**
   * This helps achieve the highlight effect for tooltips/popovers.
   *
   * 2023-07-17
   */
  const balloonHighlightStyles = {
    ...balloonStyles,
    transform: 'translate(0, -0.75px)',
    filter: 'brightness(1.75)',
  } as const;

  const svgMarkup = (
    <svg
      key="balloon"
      id={id}
      viewBox={`0 0 ${style.width} ${style.height}`}
      style={balloonStyles}
      // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string | undefined'.
      preserveAspectRatio={fillParentDimensions ? 'none' : null}
    >
      <defs>
        {/* Needed to ensure HTML-like border behavior: */}
        <clipPath id={balloonClipID}>
          <path d={balloonPath} />
        </clipPath>
      </defs>

      <path
        d={balloonPath}
        // This change was made in order to make "border" (in SVG world known as `stroke`),
        // work like it would if the SVG were using `box-sizing: border-box` -- that is,
        // the border goes _inside_ the shape.

        // In SVG, the **middle** of the stroke runs along the outside edge of the fill.
        // Therefore, we double the `borderWidth` input and clip the bubble shape with itself
        // (minus the stroke) to simulate an inner border of the appropriate size.
        clipPath={`url(#${balloonClipID})`}
        style={{
          strokeWidth: `${balloonStrokeWidth}px`,
          stroke: borderColor,
        }}
        fill={caretBackgroundColor ? caretBackgroundColor : style.color}
      />
    </svg>
  );

  const svg2Markup = (
    <svg
      key="balloon-highlight"
      id={id}
      viewBox={`0 0 ${style.width} ${style.height}`}
      style={balloonHighlightStyles}
      // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string | undefined'.
      preserveAspectRatio={fillParentDimensions ? 'none' : null}
    >
      <defs>
        {/* Needed to ensure HTML-like border behavior: */}
        <clipPath id={balloonClipID}>
          <path d={balloonPath} />
        </clipPath>
      </defs>

      <path
        d={balloonPath}
        // This change was made in order to make "border" (in SVG world known as `stroke`),
        // work like it would if the SVG were using `box-sizing: border-box` -- that is,
        // the border goes _inside_ the shape.

        // In SVG, the **middle** of the stroke runs along the outside edge of the fill.
        // Therefore, we double the `borderWidth` input and clip the bubble shape with itself
        // (minus the stroke) to simulate an inner border of the appropriate size.
        clipPath={`url(#${balloonClipID})`}
        style={{
          strokeWidth: `${balloonStrokeWidth}px`,
          stroke: borderColor,
        }}
        fill={style.color}
      />
    </svg>
  );

  return (
    <>
      {svg2Markup}
      {svgMarkup}
    </>
  );
};

export default Balloon;
